import { PageController } from '@client/components/_core/layout/PageController';
import { LearnedDecisionsCard } from '@client/components/_shared/auto-detect/LearnedDecisionsCard';
import { Chart } from '@client/components/_ui/data-display/Chart';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { Dashboard } from '@client/components/_ui/display/dashboard/Dashboard';
import { errorNotification } from '@client/components/_ui/feedback/ErrorNotification';
import { ModalFooter } from '@client/components/_ui/feedback/ModalFooter';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { downloadBlob } from '@client/utils';
import { MESSAGE_CLASSIFICATION_EXPORT } from '@shared/constants/business/message-classification';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { Col, Form, Modal, Row, Typography } from 'antd';
import { Download } from 'lucide-react';
import { DateTime } from 'luxon';
import { useId, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/tools/auto-detect/dashboard')({
    staticData: {
        breadcrumb: {
            title: 'Dashboard',
        },
        modules: ['auto-detect'],
        permissions: [PERMISSIONS.MESSAGE_CLASSIFICATION_ACCESS],
    },
    component: ComplianceAIInboxDashboard,
});

type ExportInput = RouterInputs['messageClassification']['listMessageClassificationForExport'];
type Filters = {
    dateFrom: ExportInput['dateFrom'] | null;
    dateTo: ExportInput['dateTo'] | null;
};

const FormItem = Form.Item<Filters>;

type ExportedItem = RouterOutputs['messageClassification']['listMessageClassificationForExport'][number];

/**
 * Derive a single-cell label for the export from the two independent
 * classification axes. Matches the server-side `deriveClassificationLabel`
 * helper used in PostHog events so analytics + CSV exports stay aligned.
 */
function classificationLabel(item: Pick<ExportedItem, 'isIncident' | 'isComplaint'>): string {
    if (item.isIncident && item.isComplaint) return 'Incident and complaint';
    if (item.isIncident) return 'Incident';
    if (item.isComplaint) return 'Complaint';
    return 'No issue';
}

const confidenceLabels: Record<NonNullable<ExportedItem['confidence']>, string> = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
};

const statusLabels: Record<ExportedItem['status'], string> = {
    ERROR: 'Error',
    PENDING: 'Pending',
    PROCESSED: 'Processed',
    REVIEWED: 'Reviewed',
    SKIPPED: 'Skipped',
};

const KEYS = [
    'ID',
    'Message type',
    'Issue type',
    'Is a breach',
    'Certainty',
    'Status',
    'Date uploaded',
    'Uploaded by',
    'Reviewed by',
    'Breached regulation',
    'Justification',
] as const;

function shiftCalendarDate(date: string, days: number): string {
    return DateTime.fromISO(date, { zone: 'utc' }).plus({ days }).toISODate() ?? date;
}

function DownloadReport() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { formatDate, formatNumber } = useFormatter();

    const [open, setOpen] = useState(false);

    const [form] = Form.useForm<Filters>();
    const dateFrom = Form.useWatch(({ dateFrom }) => dateFrom, form);
    const dateTo = Form.useWatch(({ dateTo }) => dateTo, form);

    async function exportReport({ dateFrom, dateTo }: Filters) {
        // Both fields carry `required` rules, so onFinish always has them —
        // this guard only narrows the optional Filters type.
        if (!dateFrom || !dateTo) return;
        try {
            message.loading('Generating report...');

            const data = await trpcUtils.messageClassification.listMessageClassificationForExport.fetch({
                dateFrom,
                dateTo,
            });

            const rows = data.map(
                (item) =>
                    ({
                        ID: item.id,
                        'Message type': item.messageType,
                        'Issue type': classificationLabel(item),
                        'Is a breach': item.isBreachOfRegulation ? 'Breach' : 'Not a breach',
                        Certainty: item.confidence ? confidenceLabels[item.confidence] : '',
                        Status: statusLabels[item.status],
                        'Date uploaded': formatDate(item.uploadedAt),
                        'Uploaded by': item.UploadedBy.fullName,
                        'Reviewed by': item.ReviewedBy?.fullName || '-',
                        'Breached regulation': item.breachedRegulation || '-',
                        Justification: item.justification || '-',
                    }) satisfies Record<(typeof KEYS)[number], any>,
            );

            const { parse } = await import('json2csv');
            const csv = parse(rows, { fields: KEYS as any as string[] });

            downloadBlob(
                new Blob([csv], { type: 'text/csv;charset=utf-8' }),
                `classification-report-${formatDate(new Date())}.csv`,
            );

            message.success('Report generated successfully.');
            setOpen(false);
        } catch (e) {
            errorNotification(e as Error);
        }
    }

    return (
        <>
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                title="Download report"
                footer={<ModalFooter formId={formId} onCancel={() => setOpen(false)} okText="Create report" />}
            >
                <Typography.Paragraph>
                    Create a report of all issues detected by the Curium Auto-detect tool between the following dates.
                    Reports are limited to{' '}
                    {formatNumber(MESSAGE_CLASSIFICATION_EXPORT.MAX_ROWS, { maximumFractionDigits: 0 })} messages and a{' '}
                    {MESSAGE_CLASSIFICATION_EXPORT.MAX_RANGE_DAYS}-day range.
                </Typography.Paragraph>
                <Form id={formId} form={form} layout="vertical" onFinish={exportReport}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <FormItem
                                label="Date from"
                                name="dateFrom"
                                rules={[{ required: true, message: 'Select a start date' }]}
                            >
                                <DatePicker
                                    className="w-full"
                                    minDate={
                                        dateTo
                                            ? shiftCalendarDate(
                                                  dateTo,
                                                  -(MESSAGE_CLASSIFICATION_EXPORT.MAX_RANGE_DAYS - 1),
                                              )
                                            : undefined
                                    }
                                    maxDate={dateTo ?? undefined}
                                    outputFormat="calendar"
                                    allowClear
                                />
                            </FormItem>
                        </Col>
                        <Col span={12}>
                            <FormItem
                                label="Date to"
                                name="dateTo"
                                rules={[{ required: true, message: 'Select an end date' }]}
                            >
                                <DatePicker
                                    className="w-full"
                                    minDate={dateFrom ?? undefined}
                                    maxDate={
                                        dateFrom
                                            ? shiftCalendarDate(
                                                  dateFrom,
                                                  MESSAGE_CLASSIFICATION_EXPORT.MAX_RANGE_DAYS - 1,
                                              )
                                            : undefined
                                    }
                                    outputFormat="calendar"
                                    allowClear
                                />
                            </FormItem>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            <Button type="secondary" icon={<Download />} onClick={() => setOpen(true)}>
                Download report
            </Button>
        </>
    );
}

function ComplianceAIInboxDashboard() {
    const [messageTypeFilter, setMessageTypeFilter] = useState<'ALL' | 'EMAIL' | 'SMS'>('ALL');

    const { data, isPending } = trpc.dashboard.complianceAIInboxDashboard.useQuery(
        messageTypeFilter !== 'ALL' ? { messageType: messageTypeFilter } : undefined,
    );

    return (
        <PageController route={Route}>
            <div className="mb-4 flex items-center justify-between">
                <SegmentedControl
                    value={messageTypeFilter}
                    onChange={setMessageTypeFilter}
                    options={[
                        { label: 'All', value: 'ALL' as const },
                        { label: 'Emails', value: 'EMAIL' as const },
                        { label: 'SMS', value: 'SMS' as const },
                    ]}
                    size="middle"
                />
                <DownloadReport />
            </div>

            <Dashboard.Row>
                <Dashboard.Item title="Messages scanned last week" colSpan={12}>
                    <Chart
                        loading={isPending}
                        height={350}
                        items={[
                            {
                                type: 'interval',
                                data: data?.emailClassificationsInLastWeek || [],
                                encode: { x: 'day', y: 'count', color: 'type' },
                                transform: [{ type: 'stackY' }],
                                axis: {
                                    y: { title: 'Number of messages' },
                                },
                                labels: ['percentage', 'total'],
                            },
                        ]}
                    />
                </Dashboard.Item>
                <Dashboard.Item title="Messages scanned last 6 months" colSpan={12}>
                    <Chart
                        loading={isPending}
                        height={350}
                        items={[
                            {
                                type: 'interval',
                                data: data?.emailClassificationsInLast6Month || [],
                                encode: { x: 'month', y: 'count', color: 'type' },
                                transform: [{ type: 'stackY' }],
                                axis: {
                                    y: { title: 'Number of messages' },
                                },
                                labels: ['percentage', 'total'],
                            },
                        ]}
                    />
                </Dashboard.Item>
                <Dashboard.Item title="Confirmed issues by month" colSpan={12}>
                    <Chart
                        loading={isPending}
                        height={350}
                        items={[
                            {
                                type: 'line',
                                data: data?.confirmedIssuesByMonth || [],
                                encode: { x: 'month', y: 'count', color: 'type' },
                                axis: { y: { title: 'Number of issues' } },
                            },
                            {
                                type: 'point',
                                data: data?.confirmedIssuesByMonth || [],
                                encode: { x: 'month', y: 'count', color: 'type' },
                            },
                        ]}
                    />
                </Dashboard.Item>
            </Dashboard.Row>
            {/* CUR-886 — read-only view of the org's learned decisions so
                reviewers (who cannot open configuration) can see what the
                classifier has been taught. */}
            <div className="mt-4">
                <LearnedDecisionsCard managed={false} />
            </div>
        </PageController>
    );
}
